/**
 * Tournament (bracket) real-time – Ably.
 * Subscribes to tournament:${tournamentId} for bracket updates.
 * Socket.IO has been removed; use Ably only. See docs/ABLY_CUSTOMER_PORTAL.md.
 */
import { subscribeToChannel } from './ablyRealtime';
import { getUserIdFromToken } from '@/utils/jwt';
import { apiService } from './api';

const TOURNAMENT_EVENTS = [
  'tournament:update',
  'tournament:status',
  'match:update',
  'match:started',
  'match:completed',
  'match:cancelled',
  'round:update',
  'round:created',
  'round:deleted',
  'round:completed',
  'player:registered',
  'player:added',
  'player:removed',
  'players:moved_to_round',
  'players:removed_from_round',
  'killer_game:update',
  'tournament_chat:notification',
] as const;

export class TournamentWebSocketService {
  private eventHandlers: Map<string, Set<Function>> = new Map();
  private unsubscribe: (() => void) | null = null;
  private tournamentId: string | null = null;

  connect(tournamentId: string): void {
    if (this.tournamentId === tournamentId && this.unsubscribe) return;
    this.disconnect();

    const { accessToken } = apiService.getTokens();
    if (!accessToken) {
      console.warn('[TournamentWS] No access token');
      return;
    }

    getUserIdFromToken(accessToken); // ensure we have a user

    this.tournamentId = tournamentId;
    const channelName = `tournament:${tournamentId}`;

    const emit = (event: string, data: any) => {
      this.eventHandlers.get(event)?.forEach((h) => {
        try {
          h(data);
        } catch (e) {
          console.error('[TournamentWS] handler error', e);
        }
      });
    };

    this.unsubscribe = subscribeToChannel(
      channelName,
      TOURNAMENT_EVENTS as unknown as readonly string[],
      emit
    );
  }

  on(event: string, handler: Function): void {
    if (!this.eventHandlers.has(event)) this.eventHandlers.set(event, new Set());
    this.eventHandlers.get(event)!.add(handler);
  }

  off(event: string, handler: Function): void {
    this.eventHandlers.get(event)?.delete(handler);
  }

  isConnected(): boolean {
    return this.unsubscribe != null && this.tournamentId != null;
  }

  disconnect(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    this.tournamentId = null;
    this.eventHandlers.clear();
  }
}

export const tournamentWebSocketService = new TournamentWebSocketService();
